/**
 * Product specification pill tooltips.
 *
 * Uses one delegated document lifecycle so dynamically replaced Theme Editor
 * sections and repeated blocks cannot register duplicate listeners.
 */

(() => {
  const RUNTIME_KEY = '__MarineMaxProductGridIconBoxTooltips';
  const VALUE_SELECTOR = '.grid-icon-box__value[data-tooltip]';
  const existingRuntime = window[RUNTIME_KEY];

  if (existingRuntime?.initialize) {
    existingRuntime.initialize();
    return;
  }

  let abortController = null;
  let tooltip = null;

  function getTooltip() {
    if (tooltip?.isConnected) return tooltip;

    tooltip = document.createElement('div');
    tooltip.className = 'chip-tooltip';
    document.body.appendChild(tooltip);
    return tooltip;
  }

  function getValueElement(event) {
    const valueElement = event.target?.closest?.(VALUE_SELECTOR);
    if (!valueElement) return null;

    if (
      event.relatedTarget &&
      valueElement.contains?.(event.relatedTarget)
    ) {
      return null;
    }

    return valueElement;
  }

  function showTooltip(event) {
    const valueElement = getValueElement(event);
    if (!valueElement) return;

    const textElement = valueElement.querySelector(
      '.grid-icon-box__value-text',
    );
    if (!textElement || textElement.scrollWidth <= textElement.clientWidth) {
      return;
    }

    const text = valueElement.getAttribute('data-tooltip');
    if (!text) return;

    const nextTooltip = getTooltip();
    nextTooltip.textContent = text;
    nextTooltip.classList.remove('is-visible');
    nextTooltip.style.left = '0';
    nextTooltip.style.top = '0';
    nextTooltip.style.display = 'block';

    const valueRect = valueElement.getBoundingClientRect();
    const tooltipRect = nextTooltip.getBoundingClientRect();
    const maximumLeft = window.innerWidth - 4 - tooltipRect.width;
    const centeredLeft =
      valueRect.left + valueRect.width / 2 - tooltipRect.width / 2;
    const left = Math.max(4, Math.min(centeredLeft, maximumLeft));
    const preferredTop = valueRect.top - tooltipRect.height - 8;
    const top = preferredTop < 4 ? valueRect.bottom + 8 : preferredTop;

    nextTooltip.style.left = `${left}px`;
    nextTooltip.style.top = `${top}px`;
    window.requestAnimationFrame(() => {
      if (nextTooltip === tooltip) {
        nextTooltip.classList.add('is-visible');
      }
    });
  }

  function hideTooltip(event) {
    if (!getValueElement(event) || !tooltip) return;

    tooltip.classList.remove('is-visible');
    tooltip.style.display = 'none';
  }

  function initialize() {
    if (abortController) return;

    abortController = new AbortController();
    const listenerOptions = { signal: abortController.signal };
    document.addEventListener('mouseover', showTooltip, listenerOptions);
    document.addEventListener('mouseout', hideTooltip, listenerOptions);
    document.addEventListener('focusin', showTooltip, listenerOptions);
    document.addEventListener('focusout', hideTooltip, listenerOptions);
  }

  function destroy() {
    abortController?.abort();
    abortController = null;
    tooltip?.remove();
    tooltip = null;
  }

  const runtime = Object.freeze({ destroy, initialize });
  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: true,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  initialize();
})();
